import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import PostCard from "../components/PostCard";
import CommentsDrawer from "../components/CommentsDrawer";
import FeaturedRoomsStrip from "../components/FeaturedRoomsStrip";
import TrendingVoiceDropsStrip from "../components/TrendingVoiceDropsStrip";
import StreakClaim from "../components/StreakClaim";
import { Link } from "react-router-dom";
import { Broadcast, MagnifyingGlass } from "@phosphor-icons/react";

const FALLBACKS = [
  "https://images.unsplash.com/photo-1767801829041-f5243a34525d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1080",
  "https://images.unsplash.com/photo-1621826269707-5b27afe47c79?crop=entropy&cs=srgb&fm=jpg&q=85&w=1080",
  "https://images.unsplash.com/photo-1734448824631-458e1b9a8b5a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1080",
];

// Module-level stale-while-revalidate cache: instant paint on back-navigation,
// silent refresh in the background.
let feedCache = null;

export default function Feed() {
  const [posts, setPosts] = useState(feedCache);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/posts/feed");
      feedCache = data;
      setPosts(data);
    } catch (err) {
      console.error("feed load failed", err);
      if (!feedCache) setPosts([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="relative h-[100dvh] w-full overflow-hidden bg-ink">
      {/* Top header */}
      <header className="absolute top-0 inset-x-0 z-30 flex items-center justify-between px-5 pt-5">
        <Link to="/live" data-testid="header-go-live" className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-papaya text-ink text-xs font-black tracking-[0.2em] uppercase hover:bg-papaya-hover transition-colors animate-pulse-glow">
          <Broadcast size={14} weight="fill" />
          Go Live
        </Link>
        <div className="flex flex-col items-center">
          <h1 className="text-cream font-black text-lg tracking-tighter">Giffwurldaunce</h1>
          <div className="jam-stripe h-[3px] w-16 rounded-full mt-1" aria-hidden />
        </div>
        <Link to="/explore" data-testid="header-search" className="w-10 h-10 rounded-full crystal-glass flex items-center justify-center text-cream hover:text-lignum transition-colors">
          <MagnifyingGlass size={18} weight="bold" />
        </Link>
      </header>

      {/* Featured Rooms discovery strip — surfaces top active public/paid rooms
          on the Home feed. Renders as an overlay above the vertical snap feed. */}
      <FeaturedRoomsStrip />
      <TrendingVoiceDropsStrip />
      <StreakClaim />

      {/* Feed */}
      {posts === null ? (
        <div className="h-full flex items-center justify-center text-cream/40 text-xs tracking-[0.3em] uppercase">Loading feed…</div>
      ) : posts.length === 0 ? (
        <EmptyFeed />
      ) : (
        <div className="feed-snap h-[100dvh] overflow-y-scroll">
          {posts.map((p, i) => (
            <PostCard
              key={p.id}
              post={p}
              fallbackImage={FALLBACKS[i % FALLBACKS.length]}
              onOpenComments={setActive}
              onUpdate={load}
            />
          ))}
        </div>
      )}
      {active && <CommentsDrawer post={active} onClose={() => setActive(null)} onAdded={load} />}
    </div>
  );
}

function EmptyFeed() {
  return (
    <div className="h-full flex flex-col items-center justify-center px-8 text-center">
      <div className="text-7xl mb-4">🌴</div>
      <h2 className="text-cream font-black text-3xl tracking-tighter">Welcome to Giffwurldaunce</h2>
      <p className="text-cream/60 mt-3 max-w-sm text-sm leading-relaxed">
        The dance floor is empty. Be the first to post and set the tempo for dancers around the world.
      </p>
      <Link to="/create" data-testid="empty-create-cta" className="mt-6 px-6 py-3 rounded-full bg-papaya text-ink font-black text-sm tracking-[0.2em] uppercase hover:bg-papaya-hover transition-colors">
        Drop the First Post
      </Link>
    </div>
  );
}
